/* eslint-disable @typescript-eslint/no-unused-vars */
import { memo, useMemo } from "react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { Box, SxProps, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Theme, Typography } from "@mui/material";
import type { CCustomClassData, CustomClassData, MCustomClassData, PCustomClassData } from "types";
// biome-ignore lint/style/useImportType: <explanation>
import { ClassLabel, ClassLabelKey } from "./ClassLabel";
import styles from "./styles";

interface CustomClassTableProps {
  data: CustomClassData | undefined;
  sx?: SxProps<Theme>;
}

/**
 * CustomClassTable component renders a table displaying custom class data based on the class type.
 * It filters out specific fields from the data based on the class type and displays the remaining fields in a table format.
 *
 * @component
 * @param {CustomClassTableProps} props - The properties for the CustomClassTable component.
 * @param {CustomClassData} props.data - The data to be displayed in the table.
 *
 * @returns {JSX.Element | null} The rendered CustomClassTable component or null if no data is provided.
 *
 * @example
 * <CustomClassTable data={customClassData} />
 */
const CustomClassTable: React.FC<CustomClassTableProps> = ({ data, sx = {} }) => {
  const { t } = useTranslation();

  const { header, rows } = useMemo(() => {
    if (!data) {
      return {
        header: [],
        rows: [],
      };
    }

    const { class_type, ...rest } = data;

    let dataToBeDisplayed = {} as Partial<CustomClassData>;

    if (class_type === "M") {
      const { Rei, ...mClassData } = rest as Partial<MCustomClassData>;
      dataToBeDisplayed = mClassData;
    }

    if (class_type === "P") {
      const { Escm_min, Evm_min, fTI, ...pClassData } = rest as Partial<PCustomClassData>;
      dataToBeDisplayed = pClassData;
    }

    if (class_type === "C") {
      const { fTI, ...cClassData } = rest as Partial<CCustomClassData>;
      dataToBeDisplayed = cClassData;
    }

    return {
      header: Object.keys(dataToBeDisplayed),
      rows: Object.entries(dataToBeDisplayed),
    };
  }, [data]);

  if (!data) return null;

  return (
    <Box sx={{ ...styles.container, ...sx }} className="RlxCustomClassTable">
      <Typography variant="h5" sx={styles.title}>
        {t("Custom Class")}
      </Typography>

      <TableContainer sx={styles.wrapper}>
        <Table aria-label="custom classes table">
          <TableHead>
            <TableRow>
              {header.map((key) => (
                <TableCell key={key} align="center">
                  <ClassLabel labelKey={key as ClassLabelKey} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {rows.map(([key, value]) => (
                <TableCell key={key} align="center">
                  {value}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

CustomClassTable.displayName = "CustomClassTable";

export default memo(CustomClassTable);
